import { Autocomplete, Checkbox, FormControl, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

export default function ITAutocomplete({
  options,
  value,
  onChange,
  label,
  placeholder,
  loading,
  onOpen,
  onClose,
  onSearchChange,
  filterOptions,
  getOptionLabel,
  getOptionIdentifier,
  multiple,
  disableCloseOnSelect,
  disabled,
  hasError,
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    onOpen();
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSearchChange = (e) => {
    const searchValue = e.currentTarget.value;
    setSearch(searchValue);
    onSearchChange(searchValue, open);
  };

  const handleChange = (_, newValue) => {
    if (typeof onChange !== 'function') return;
    onChange(newValue);
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  return (
    <Autocomplete
      className={`ITAutocomplete${hasError ? ' ITAutocomplete--error' : ''}${
        disabled ? ' ITAutocomplete--disabled' : ''
      }`}
      multiple={multiple}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      limitTags={2}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(o, v) => getOptionIdentifier(o) === getOptionIdentifier(v)}
      value={value}
      onChange={handleChange}
      disableCloseOnSelect={disableCloseOnSelect}
      filterOptions={filterOptions ? undefined : (x) => x}
      loading={loading}
      disabled={disabled}
      renderOption={(optionProps, option, { selected }) => {
        const { key } = optionProps;
        const className = `${optionProps.className} ITAutocomplete__option`;
        return (
          <li key={key} {...optionProps} className={className}>
            {multiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {getOptionLabel(option)}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          className="ITAutocomplete__input"
          value={search}
          onChange={handleSearchChange}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
    />
  );
}

ITAutocomplete.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onSearchChange: PropTypes.func,
  filterOptions: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  getOptionIdentifier: PropTypes.func,
  multiple: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
};

ITAutocomplete.defaultProps = {
  options: [],
  value: null,
  onChange: () => {},
  label: '',
  placeholder: '',
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  onSearchChange: () => {},
  filterOptions: true,
  getOptionLabel: (option) => (typeof option === 'string' ? option : option.label),
  getOptionIdentifier: (option) => (typeof option === 'string' ? option : option.value),
  multiple: false,
  disableCloseOnSelect: false,
  disabled: false,
  hasError: false,
};
